import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import { api } from "./api.js";
import { logError } from "./logger.js";
import { categoryOf, isoDate, toneOf, type CalendarTodo, type TodoDraft, normalizeTodo} from "./calendarTodos.js";

// ─── One shared store ─────────────────────────────────────────────────────────
// The Leave > Calendar and the dashboard "My Todos" widget both read this single
// module-level cache, so a tick or an add made in one shows in the other at once
// with no reload. Months are the cache unit; a range request fetches only the
// months it is missing.

const monthKey = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, "0")}`;

let todos: CalendarTodo[] = [];
const fetchedMonths = new Set<string>();
const listeners = new Set<() => void>();

function emit(next: CalendarTodo[]) {
  todos = next;
  listeners.forEach(l => l());
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getSnapshot = () => todos;

/** The months an inclusive "YYYY-MM-DD" range touches. */
function monthsIn(from: string, to: string): Array<{ y: number; m: number }> {
  const out: Array<{ y: number; m: number }> = [];
  let y = Number(from.slice(0, 4)), m = Number(from.slice(5, 7)) - 1;
  const ey = Number(to.slice(0, 4)), em = Number(to.slice(5, 7)) - 1;
  while (y < ey || (y === ey && m <= em)) {
    out.push({ y, m });
    m++; if (m > 11) { m = 0; y++; }
  }
  return out;
}

const inFlight = new Map<string, Promise<void>>();

/** Fetches whichever months of [from, to] are not cached yet (or all, when forced). */
export async function ensureTodoRange(from: string, to: string, force = false): Promise<void> {
  const months = monthsIn(from, to);
  const missing = force ? months : months.filter(({ y, m }) => !fetchedMonths.has(monthKey(y, m)));
  if (missing.length === 0) return;
  const first = missing[0]!, last = missing[missing.length - 1]!;
  const lo = isoDate(first.y, first.m, 1);
  const hi = isoDate(last.y, last.m, new Date(last.y, last.m + 1, 0).getDate());
  const key = `${lo}:${hi}`;
  const existing = inFlight.get(key);
  if (existing && !force) return existing;
  const p = (async () => {
    const res = await api.get<CalendarTodo[]>(`/me/calendar-notes?from=${lo}&to=${hi}`);
    // Replace the fetched window, keep everything outside it (and unsaved temp rows).
    emit([
      ...todos.filter(t => { const k = t.date.split("T")[0]!; return k < lo || k > hi || t.id.startsWith("tmp-"); }),
      ...res.data.map(normalizeTodo),
    ]);
    missing.forEach(({ y, m }) => fetchedMonths.add(monthKey(y, m)));
  })().finally(() => { inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}

export function isRangeLoaded(from: string, to: string): boolean {
  return monthsIn(from, to).every(({ y, m }) => fetchedMonths.has(monthKey(y, m)));
}

function draftToBody(d: Partial<TodoDraft>) {
  const body: Record<string, unknown> = {};
  if (d.title !== undefined) body.title = d.title.trim();
  if (d.description !== undefined) body.description = d.description;
  if (d.category !== undefined) body.tone = toneOf(d.category);
  if (d.tags !== undefined) body.tags = d.tags;
  if (d.time !== undefined) body.time = d.time;
  if (d.date !== undefined) body.date = d.date;
  return body;
}

// Writes are optimistic: the store updates first and rolls back when the request fails.
async function createTodo(draft: TodoDraft): Promise<CalendarTodo> {
  const now = new Date().toISOString();
  const temp: CalendarTodo = {
    id: `tmp-${Math.random().toString(36).slice(2)}`, date: draft.date, title: draft.title.trim(), description: draft.description,
    tone: toneOf(draft.category), tags: draft.tags, time: draft.time, done: false, doneAt: null, createdAt: now, updatedAt: now,
  };
  emit([...todos, temp]);
  try {
    const res = await api.post<CalendarTodo>("/me/calendar-notes", draftToBody(draft));
    const saved = normalizeTodo(res.data);
    emit(todos.map(t => (t.id === temp.id ? saved : t)));
    return saved;
  } catch (err) {
    emit(todos.filter(t => t.id !== temp.id));
    throw err;
  }
}

async function patchTodo(id: string, changes: Partial<CalendarTodo>, body: Record<string, unknown>): Promise<void> {
  const before = todos.find(t => t.id === id);
  if (!before) return;
  emit(todos.map(t => (t.id === id ? { ...t, ...changes } : t)));
  try {
    const res = await api.patch<CalendarTodo>(`/me/calendar-notes/${id}`, body);
     emit(todos.map(t => (t.id === id ? normalizeTodo(res.data) : t)));
  } catch (err) {
    emit(todos.map(t => (t.id === id ? before : t)));
    throw err;
  }
}

async function deleteTodo(id: string): Promise<CalendarTodo | null> {
  const before = todos.find(t => t.id === id);
  if (!before) return null;
  emit(todos.filter(t => t.id !== id));
  try {
    await api.delete(`/me/calendar-notes/${id}`);
    return before;
  } catch (err) {
    emit([...todos, before]);
    throw err;
  }
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Every cached todo, re-rendering when any writer (calendar or widget) changes the cache. */
export function useTodoStore(): CalendarTodo[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Loads [from, to] into the shared cache. Refetches on window focus (no polling),
 * and after idle prefetches `prefetch` ranges (e.g. the previous and next period).
 */
export function useTodosRange(from: string, to: string, enabled = true, prefetch: Array<[string, string]> = []) {
  const all = useTodoStore();
  const [loading, setLoading] = useState(() => enabled && !isRangeLoaded(from, to));
  const [loadError, setLoadError] = useState(false);
  const latest = useRef({ from, to });
  latest.current = { from, to };
  const prefetchKey = prefetch.map(p => p.join(":")).join("|");

  const load = useCallback(async (force = false) => {
    const { from: f, to: t } = latest.current;
    if (!force && isRangeLoaded(f, t)) { setLoading(false); setLoadError(false); return; }
    setLoading(true);
    setLoadError(false);
    try {
      await ensureTodoRange(f, t, force);
    } catch (err) {
      logError("useTodosRange.load", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) void load(); }, [enabled, load, from, to]);

  useEffect(() => {
    if (!enabled || !prefetchKey) return;
    const run = () => prefetch.forEach(([f, t]) => { void ensureTodoRange(f, t).catch(() => {}); });
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number; cancelIdleCallback?: (id: number) => void };
    if (w.requestIdleCallback) { const id = w.requestIdleCallback(run); return () => w.cancelIdleCallback?.(id); }
    const id = window.setTimeout(run, 400);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, prefetchKey]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => { void load(true); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, load]);

  return { todos: all, loading, loadError, ready: isRangeLoaded(from, to), reload: () => load(true) };
}

/** Write actions bound to the shared store; failures roll back, then report via `onWriteError`. */
export function useTodoActions(onWriteError: (message: string) => void) {
  const onErr = useRef(onWriteError);
  onErr.current = onWriteError;

  const guard = useCallback(async <T,>(where: string, fn: () => Promise<T>): Promise<T> => {
    try { return await fn(); }
    catch (err) {
      logError(where, err);
      onErr.current("Couldn't save. Try again.");
      throw err;
    }
  }, []);

  const create = useCallback((draft: TodoDraft) => guard("todos.create", () => createTodo(draft)), [guard]);

  const update = useCallback((id: string, draft: TodoDraft) => guard("todos.update", () => patchTodo(id, {
    title: draft.title.trim(), description: draft.description, tone: toneOf(draft.category), tags: draft.tags, time: draft.time, date: draft.date,
  }, draftToBody(draft))), [guard]);

  const setDone = useCallback((id: string, done: boolean) => guard("todos.setDone", () => patchTodo(
    id, { done, doneAt: done ? new Date().toISOString() : null }, { done },
  )), [guard]);

  /** Returns the removed todo so the caller can offer Undo. */
  const remove = useCallback((id: string) => guard("todos.remove", () => deleteTodo(id)), [guard]);

  /** Re-creates a deleted todo (Undo). Done state and the original fields are kept. */
  const restore = useCallback(async (t: CalendarTodo) => {
    const created = await create({
      title: t.title, description: t.description, category: categoryOf(t.tone), tags: t.tags, time: t.time, date: t.date.split("T")[0]!,
    });
    if (t.done) await setDone(created.id, true);
  }, [create, setDone]);

  /** Moves the given todos to `date`; resolves to the previous dates so the move can be undone. */
  const moveTo = useCallback(async (ids: string[], date: string): Promise<Map<string, string>> => {
    const prevDates = new Map<string, string>();
    for (const id of ids) {
      const t = todos.find(x => x.id === id);
      if (t) prevDates.set(id, t.date.split("T")[0]!);
    }
    await Promise.all(ids.map(id => guard("todos.move", () => patchTodo(id, { date }, { date }))));
    return prevDates;
  }, [guard]);

  const restoreDates = useCallback(async (prevDates: Map<string, string>) => {
    await Promise.all([...prevDates].map(([id, date]) => guard("todos.restoreDate", () => patchTodo(id, { date }, { date }))));
  }, [guard]);

  return { create, update, setDone, remove, restore, moveTo, restoreDates };
}

interface Options {
  year: number;
  month: number; // 0-11
  enabled: boolean;
  /** Called with a human message when an optimistic write had to be rolled back. */
  onWriteError: (message: string) => void;
}

/** The Calendar's view: the visible month plus the previous and next one. */
export function useCalendarTodos({ year, month, enabled, onWriteError }: Options) {
  const prev = new Date(year, month - 1, 1);
  const from = isoDate(prev.getFullYear(), prev.getMonth(), 1);
  const lastOfNext = new Date(year, month + 2, 0);
  const to = isoDate(lastOfNext.getFullYear(), lastOfNext.getMonth(), lastOfNext.getDate());
  const range = useTodosRange(from, to, enabled);
  const actions = useTodoActions(onWriteError);
  return { ...range, ...actions };
}
